import { useCallback, useContext } from 'react';
import { matchPath } from 'react-router';

import { AccessContext, type AccessContextValue } from '@/contexts/AccessContext';
import { ROUTE_MODULES, routeModules, type RouteAccess } from '@/lib/modules';

/**
 * Nested routes behind a `RequireAdminRoute` inside a module route.
 * `ROUTE_MODULES` keys top-level routes only, so it cannot express them.
 */
const NESTED_ADMIN_PATHS: readonly string[] = ['/billing/reconciliation'];

interface RoutePattern {
  pattern: string;
  access: RouteAccess;
  segments: number;
  staticSegments: number;
}

const ROUTE_PATTERNS: readonly RoutePattern[] = [
  ...Object.entries(ROUTE_MODULES).map(([pattern, access]) => ({ pattern, access })),
  ...NESTED_ADMIN_PATHS.map((pattern) => ({ pattern, access: 'admin' as const })),
].map(({ pattern, access }) => {
  const parts = pattern.split('/').filter(Boolean);
  return {
    pattern,
    access,
    segments: parts.length,
    staticSegments: parts.filter((part) => !part.startsWith(':')).length,
  };
});

/** The pathname of a link target: no query, no hash, no trailing slash. */
function pathnameOf(path: string): string {
  const pathname = path.split(/[?#]/, 1)[0] || '/';
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

/**
 * The access rule that guards `path`, or `undefined` when no route rule
 * covers it. An exact match wins over a prefix match (the nested `billing` and
 * `settings` trees are keyed by their root); among equals, more segments win,
 * then more static segments, as the router ranks `/prospects/new` above
 * `/prospects/:id`. `/` matches only itself.
 */
export function routeAccessOf(path: string): RouteAccess | undefined {
  const pathname = pathnameOf(path);
  let best: { rule: RoutePattern; score: number } | undefined;
  for (const rule of ROUTE_PATTERNS) {
    const exact = matchPath({ path: rule.pattern, end: true }, pathname) !== null;
    const prefix =
      !exact && rule.pattern !== '/' && matchPath({ path: rule.pattern, end: false }, pathname) !== null;
    if (!exact && !prefix) continue;
    const score = (exact ? 10_000 : 0) + rule.segments * 100 + rule.staticSegments;
    if (!best || score > best.score) best = { rule, score };
  }
  return best?.rule.access;
}

/**
 * May the session open `path`? Admin: always. Employee: when it holds a module
 * of the route rule. A path no rule covers is allowed, so a link is never
 * hidden from what the router may still serve (the `/me` portal, say).
 */
export function canOpenPath(
  path: string,
  access: Pick<AccessContextValue, 'isAdmin' | 'hasModule'>,
): boolean {
  return accessAllows(routeAccessOf(path), access);
}

/**
 * Does an access rule admit the session? Admin: always. No rule: everyone.
 * `admin`: admins only. Otherwise any module of the rule. Shared by the route
 * guards' link check and the platform guide's topic filter.
 */
export function accessAllows(
  rule: RouteAccess | undefined,
  access: Pick<AccessContextValue, 'isAdmin' | 'hasModule'>,
): boolean {
  if (access.isAdmin) return true;
  if (rule === undefined) return true;
  if (rule === 'admin') return false;
  return routeModules(rule).some(access.hasModule);
}

/**
 * useCanOpen returns `(path) => boolean`: true when the route guards would let
 * the session open `path` (issue #1670 module model). Links, entity links and
 * the command palette use it so an employee never follows a link that bounces
 * them to another page. UX only; the server module gates are the boundary.
 *
 * Outside an `AccessProvider` every path opens, like `useModuleGate`, so
 * components rendered in isolation keep their links.
 */
export function useCanOpen(): (path: string) => boolean {
  const access = useContext(AccessContext);
  return useCallback((path: string) => (access ? canOpenPath(path, access) : true), [access]);
}
